import type { Note } from '@just-us/shared'
/**
 * pages/note-sent.tsx
 *
 * Sent notes list.
 * - Read receipt: "Read ✓✓" (pink) if opened_at, "Delivered ✓" (muted) if not
 * - Tap card → /notes/:id
 */
import { useNavigate } from 'react-router-dom'
import BottomNav from '../components/bottom-nav'
import { useNotes } from '../hooks/use-notes'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const timeAgo = (unix: number): string => {
  const diffSec = Math.floor(Date.now() / 1000) - unix
  if (diffSec < 60) return 'just now'
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  return `${diffDay}d ago`
}

const ENVELOPE_EMOJI: Record<string, string> = {
  plain: '✉️',
  heart: '💌',
  flower: '🌸',
}

const MOOD_EMOJI: Record<string, string> = {
  lovey: '💕',
  celebratory: '🎉',
  dreamy: '🌙',
  cozy: '☕',
}

// ---------------------------------------------------------------------------
// Note card
// ---------------------------------------------------------------------------

interface SentCardProps {
  note: Note
  onTap: (id: string) => void
}

const SentCard = ({ note, onTap }: SentCardProps) => {
  const isRead = !!note.opened_at

  return (
    <button
      type="button"
      onClick={() => onTap(note.id)}
      className="w-full text-left bg-white rounded-[16px] border border-[#F9E0E8] shadow-[0_2px_12px_rgba(212,83,126,0.08)] p-4 active:scale-[0.98] active:shadow-none transition-all duration-150 touch-manipulation"
    >
      <div className="flex items-start gap-3">
        {/* Envelope icon */}
        <div className="w-[40px] h-[40px] rounded-full bg-[#FBEAF0] border border-[#F4C0D1] flex items-center justify-center text-[20px] shrink-0">
          {ENVELOPE_EMOJI[note.envelope_style] ?? '✉️'}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            {note.mood && (
              <span className="font-['DM_Sans'] text-[11px] text-[#B89AA2]">
                {MOOD_EMOJI[note.mood]} {note.mood}
              </span>
            )}
            <span className="font-['DM_Sans'] text-[11px] text-[#B89AA2] ml-auto shrink-0">
              {timeAgo(note.created_at)}
            </span>
          </div>

          {/* Content preview */}
          <p className="font-['DM_Sans'] text-[14px] text-[#7A5A62] line-clamp-2 leading-[1.5]">
            {note.content ? note.content.slice(0, 80) : note.doodle_url ? '🎨 Doodle' : '…'}
          </p>

          {/* Read receipt */}
          <div className="flex items-center gap-1 mt-2">
            {isRead ? (
              <>
                <span className="font-['DM_Sans'] text-[11px] text-[#D4537E] font-medium">✓✓</span>
                <span className="font-['DM_Sans'] text-[11px] text-[#D4537E]">Read</span>
              </>
            ) : (
              <>
                <span className="font-['DM_Sans'] text-[11px] text-[#B89AA2]">✓</span>
                <span className="font-['DM_Sans'] text-[11px] text-[#B89AA2]">Delivered</span>
              </>
            )}
          </div>
        </div>
      </div>
    </button>
  )
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

const SentCardSkeleton = () => (
  <div className="w-full bg-white rounded-[16px] border border-[#F9E0E8] p-4 animate-shimmer">
    <div className="flex items-start gap-3">
      <div className="w-[40px] h-[40px] rounded-full bg-[#FBEAF0]" />
      <div className="flex-1 flex flex-col gap-2">
        <div className="h-[12px] rounded-full bg-[#F4C0D1] w-1/2" />
        <div className="h-[14px] rounded-full bg-[#F9E0E8] w-full" />
        <div className="h-[11px] rounded-full bg-[#F9E0E8] w-1/3 mt-1" />
      </div>
    </div>
  </div>
)

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function NoteSentPage() {
  const navigate = useNavigate()
  const { data, isLoading } = useNotes('sent')

  const notes: Note[] = data?.data ?? []

  return (
    <div className="min-h-screen bg-[#FAF8F4] flex flex-col max-w-[430px] mx-auto relative">
      {/* Sticky header */}
      <header className="sticky top-0 z-40 bg-[#FAF8F4] pt-[env(safe-area-inset-top)] border-b border-[#F9E0E8]">
        <div className="flex items-center justify-between px-5 h-[56px]">
          <button
            type="button"
            onClick={() => navigate('/notes')}
            className="w-[36px] h-[36px] rounded-full bg-[#FBEAF0] flex items-center justify-center active:scale-90 transition-all duration-150 touch-manipulation"
            aria-label="Back"
          >
            <span className="text-[16px]">←</span>
          </button>
          <span className="font-['Playfair_Display'] text-[18px] font-medium text-[#2C1810]">
            Sent
          </span>
          <button
            type="button"
            onClick={() => navigate('/notes/compose')}
            className="w-[36px] h-[36px] rounded-full bg-[#D4537E] flex items-center justify-center text-[18px] active:scale-90 active:bg-[#993556] transition-all duration-150 touch-manipulation shadow-[0_4px_16px_rgba(212,83,126,0.28)]"
            aria-label="Compose"
          >
            ✏️
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto px-5 pt-4 pb-[88px] flex flex-col gap-3">
        {isLoading ? (
          <>
            <SentCardSkeleton />
            <SentCardSkeleton />
          </>
        ) : notes.length === 0 ? (
          <div className="flex flex-col items-center justify-center pt-16 gap-3 animate-fade-in">
            <div className="text-[48px] animate-float">💌</div>
            <p className="font-['Playfair_Display'] text-[18px] font-medium text-[#2C1810]">
              Nothing sent yet
            </p>
            <p className="font-['DM_Sans'] text-[13px] text-[#B89AA2] text-center px-8">
              Send your first note and it will appear here.
            </p>
            <button
              type="button"
              onClick={() => navigate('/notes/compose')}
              className="mt-2 h-[44px] px-6 rounded-full bg-[#D4537E] font-['DM_Sans'] text-[14px] font-medium text-white shadow-[0_4px_16px_rgba(212,83,126,0.28)] active:scale-[0.97] active:bg-[#993556] transition-all duration-150"
            >
              Write a note
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3 animate-fade-in-up">
            {notes.map((note) => (
              <SentCard key={note.id} note={note} onTap={(id) => navigate(`/notes/${id}`)} />
            ))}
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  )
}
